import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Code,
  Paperclip,
  CalendarDays,
  Link2,
  FileText,
  X,
  Copy,
  Scissors,
  ClipboardPaste,
  TextSelect,
  Smile,
  Reply,
} from 'lucide-react';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { formatBytes, planMaxFileBytes } from '../../services/planQuota';
import chatService from '../../services/chatService';
import { sendTyping } from '../../services/chatChannelService';
import { utf8ByteLength } from '../../utils/formatBytes';
import { longMessageBytes } from '../../utils/chatComposeLimits';
import { getChannelCryptoKey, encryptFile } from '../../utils/chatCrypto';
import { prepareAttachmentFile } from '../../utils/mediaCompression';
import ContextMenu from '../ui/ContextMenu';
import EmojiReactionPicker from '../ui/EmojiReactionPicker';
import Tooltip from '../ui/Tooltip';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { copyTextToClipboard, readClipboard } from '../../utils/clipboard';
import ShareDrilldownPicker from '../ShareDrilldownPicker';
import AppointmentSharePicker from '../AppointmentSharePicker';
import { usePreferencesStore } from '../../store/preferencesStore';
import { useChatUiStore } from '../../store/chatUiStore';
import { CODE_LANGUAGE_OPTIONS } from './ChatCodeBlock';

export function Composer({ channelId, onSend }) {
  const composerUser = useAuthStore((s) => s.user);
  const replyTarget = useChatStore((s) => s.replyTarget);
  const [text, setText] = useState('');
  const [codeMode, setCodeMode] = useState(false);
  const [codeLang, setCodeLang] = useState('plaintext');
  const [showRefs, setShowRefs] = useState(false);
  const [showApptPicker, setShowApptPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // A file staged on the current message (pasted or picked) but not yet sent.
  // { file, filePromise, abort, previewUrl|null, isImage, name }. Sent
  // together with the text on submit; filePromise resolves to the (possibly
  // compressed) file to upload.
  const [pending, setPending] = useState(null);
  // Percent of an in-flight video compression, or null when none is running.
  const [compressPct, setCompressPct] = useState(null);
  // Right-click menu on the input: { x, y, items } or null.
  const [ctxMenu, setCtxMenu] = useState(null);
  const fileRef = useRef(null);

  // MIRRORS THE SERVER. The accept-list lives in backend_phoenix
  // attachments.ex (@allowed_ext) and the 25 MB cap next to it; anything else
  // 422s AFTER the upload round trip. There is no cross-repo contract test to
  // pin these together (the app's CI clones only the app), so the mirror is
  // maintained by hand — change one, change both. The tooltip and the file
  // picker's `accept` both derive from this single list so they cannot
  // disagree with each other.
  // BYTES, not characters. The server counts `byte_size(body)` against 16_000,
  // and a character threshold is only equal to it for ASCII: 10 000 characters
  // of Russian is 18 056 bytes, which sailed under a 12 000-CHARACTER limit and
  // was then rejected by the server — leaving exactly the wall of unsendable
  // text this feature exists to prevent, for every non-Latin script. CJK broke
  // above ~5 300 characters. The threshold and the two behaviours around it are
  // user-settable under Settings → Extensions → Chat; `longMessageBytes`
  // clamps, so a hand-edited preferences file cannot raise it past the server's
  // cap and re-open the failure.
  const longMessageAsFile = usePreferencesStore((s) => s.chatLongMessageAsFile);
  const showSizeIndicator = usePreferencesStore((s) => s.chatShowSizeIndicator);
  const longMessageKb = usePreferencesStore((s) => s.chatLongMessageKb);
  const PASTE_TO_FILE_BYTES = longMessageBytes(longMessageKb);
  // Recomputed per keystroke, but only over the composer's own text — cheap,
  // and the alternative (measuring on submit only) is what left people
  // discovering the cap by being refused.
  const bodyBytes = utf8ByteLength(text || '');

  const ATTACH_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.txt', '.json', '.csv', '.log', '.md', '.zip'];
  const attachTooltip =
    'Attach file — ' +
    ATTACH_EXTS.map((e) => e.slice(1).toUpperCase()).join(', ') +
    // The plan's own per-file limit (Free 5 MB, Pro/Team 25 MB, Enterprise 50 MB).
    ` · up to ${formatBytes(planMaxFileBytes(composerUser))}`;

  const lastTyping = useRef(0);
  const taRef = useRef(null);
  const emojiTriggerRef = useRef(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  const clearPending = useCallback(() => {
    setCompressPct(null);
    setPending((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      prev?.abort?.abort();
      return null;
    });
  }, []);

  const stageFile = useCallback((file) => {
    if (!file) return;
    setUploadError(false);
    const isImage = (file.type || '').startsWith('image/');
    const isVideo = (file.type || '').startsWith('video/');
    // Compress images/videos in the background while the user types the
    // caption; submit awaits the result. Aborted when the attachment is
    // removed or replaced; any failure resolves to the original file.
    const abort = new AbortController();
    setCompressPct(isVideo ? 0 : null);
    const filePromise = prepareAttachmentFile(file, {
      signal: abort.signal,
      onProgress: isVideo ? (fraction) => setCompressPct(Math.round(fraction * 100)) : undefined,
    })
      .catch(() => file)
      .then((f) => {
        if (isVideo) setCompressPct((pct) => (abort.signal.aborted ? pct : null));
        return f;
      });
    // Intrinsic dimensions ride along in the message metadata so every
    // client can reserve the image's display box before a single byte of it
    // has loaded (no reflow). Mutated in place when the probe resolves —
    // submit() reads it at send time.
    const dims = { w: 0, h: 0 };
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    if (previewUrl) {
      const probe = new Image();
      probe.onload = () => {
        dims.w = probe.naturalWidth || 0;
        dims.h = probe.naturalHeight || 0;
      };
      probe.src = previewUrl;
    }
    setPending((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      prev?.abort?.abort();
      return {
        file,
        filePromise,
        abort,
        isImage,
        dims,
        name: file.name || 'file',
        previewUrl,
      };
    });
  }, []);

  // Switching conversations: restore this channel's unsent draft (kept in
  // chatUiStore so answering a ping elsewhere doesn't destroy an in-progress
  // message), reset the transient bits, focus the input.
  useEffect(() => {
    setText(useChatUiStore.getState().draftsByChannel[channelId] || '');
    setCodeMode(false);
    setCodeLang('plaintext');
    setShowRefs(false);
    clearPending();
    if (channelId != null) taRef.current?.focus();
  }, [channelId, clearPending]);

  // Size the box to its content whenever text changes PROGRAMMATICALLY
  // (draft restore on switch, clear on send). Runs after the new value is in
  // the DOM — measuring inside the switch effect above would size against
  // the previous channel's text. Idempotent with onType's per-keystroke resize.
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [text]);

  // Focus the input the moment a reply is started, same as clicking in.
  useEffect(() => {
    if (replyTarget) taRef.current?.focus();
  }, [replyTarget]);

  // Revoke any outstanding object URL on unmount.
  useEffect(() => () => clearPending(), [clearPending]);

  const onType = (e) => {
    setText(e.target.value);
    useChatUiStore.getState().setDraft(channelId, e.target.value);
    const now = Date.now();
    if (now - lastTyping.current > 2000) {
      lastTyping.current = now;
      sendTyping(channelId);
    }
    const ta = taRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
    }
  };

  const submit = async () => {
    const body = text.trim();
    if (uploading) return;
    // Nothing to send.
    if (!body && !pending) return;

    // A staged attachment is encrypted with the channel key, uploaded, then
    // sent as one message carrying the typed text as its caption (body).
    if (pending) {
      setUploadError(false);
      setUploading(true);
      try {
        // Wait for any in-flight compression; resolves to the original file
        // when compression was skipped or failed.
        const uploadFile = (await pending.filePromise) || pending.file;
        const uploadName = uploadFile.name || pending.name;
        const cryptoKey = await getChannelCryptoKey(channelId);
        const encrypted = await encryptFile(cryptoKey, uploadFile);
        const form = new FormData();
        form.append('file', new File([encrypted], `${uploadName}.bin`, { type: 'application/octet-stream' }));
        form.append('name', uploadName);
        form.append('mime', uploadFile.type || 'application/octet-stream');
        form.append('size', String(uploadFile.size));

        const meta = await chatService.uploadAttachment(channelId, form);
        onSend(body, meta.kind === 'image' ? 'image' : 'file', {
          url: meta.url,
          name: meta.name,
          size: meta.size,
          mime: meta.mime,
          enc: meta.enc || 'aes-gcm-256',
          ...(pending.dims?.w && pending.dims?.h ? { w: pending.dims.w, h: pending.dims.h } : {}),
        });
        setText('');
        useChatUiStore.getState().setDraft(channelId, '');
        clearPending();
        if (taRef.current) taRef.current.style.height = 'auto';
      } catch (err) {
        console.warn('[chat] attachment upload failed:', err);
        setUploadError(true);
      } finally {
        setUploading(false);
      }
      return;
    }

    // TOO LONG TO SEND? Become a .txt attachment instead of failing.
    //
    // The server rejects a body over `@max_body_bytes` (16 000 BYTES). The
    // paste path already converts; TYPING past the cap did not, so a long
    // message composed in the box was simply refused — and the cap arrives
    // three times sooner for Cyrillic and five times sooner for CJK than the
    // character count on screen suggests, which is why this reads as "chat
    // does not support my language" rather than as a length limit.
    //
    // Same treatment as a long paste: the text is preserved as a file with a
    // short caption, so nothing the user wrote is lost.
    if (longMessageAsFile && utf8ByteLength(body) > PASTE_TO_FILE_BYTES) {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      stageFile(new File([body], `message-${stamp}.txt`, { type: 'text/plain' }));
      setText('');
      useChatUiStore.getState().setDraft(channelId, '');
      if (taRef.current) taRef.current.style.height = 'auto';
      return;
    }

    onSend(body, codeMode ? 'code' : 'text', codeMode ? { language: codeLang } : {});
    setText('');
    useChatUiStore.getState().setDraft(channelId, '');
    if (taRef.current) taRef.current.style.height = 'auto';
  };

  const onKeyDown = (e) => {
    // With a staged attachment, Enter sends (regardless of code mode).
    if (e.key === 'Enter' && !e.shiftKey && (!codeMode || pending)) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && codeMode) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape' && replyTarget) {
      // Esc cancels an active reply, matching Discord/Telegram.
      e.preventDefault();
      useChatStore.getState().clearReplyTarget();
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    stageFile(file);
  };

  // Paste an image straight from the clipboard (screenshot, copied image) — it is
  // staged on the current message rather than sent immediately. The clipboard
  // exposes it as a file item with no name, so synthesise one.
  const onPaste = (e) => {
    const items = e.clipboardData?.items;

    // A PASTE LONGER THAN THE SERVER WILL ACCEPT becomes a .txt attachment,
    // exactly as a pasted image becomes a file — same staging, same preview,
    // same "type a comment underneath" flow.
    //
    // Without this the message simply FAILED to send: the server rejects a body
    // over @max_body_bytes (16 KB) and the composer was left holding a wall of
    // text with no way to recover it but a manual cut-and-save. The threshold
    // is deliberately BELOW the server's limit — a body near the cap can still
    // be pushed over by a reply quote or metadata, and a paste that becomes a
    // file is recoverable while a rejected send is not.
    const pastedText = e.clipboardData?.getData('text/plain') || '';
    if (longMessageAsFile && utf8ByteLength(pastedText) > PASTE_TO_FILE_BYTES) {
      e.preventDefault();
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      stageFile(
        new File([pastedText], `pasted-text-${stamp}.txt`, { type: 'text/plain' })
      );
      return;
    }

    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (!blob) continue;
        e.preventDefault();
        const ext = (item.type.split('/')[1] || 'png').split('+')[0];
        const named =
          blob.name && blob.name !== 'image.png'
            ? blob
            : new File([blob], `pasted-${Date.now()}.${ext}`, { type: item.type });
        stageFile(named);
        return;
      }
    }
  };

  // The drilldown picker mints the scoped share link itself and hands back the
  // finished metadata; the message goes out as a `share` card.
  const sendShare = (metadata) => {
    onSend('', 'share', metadata);
    setShowRefs(false);
  };

  // Right-click menu on the composer input: Cut/Copy of the selection, Paste
  // (clipboard images are staged as attachments, like Ctrl+V), Select all.
  const onComposerContextMenu = (e) => {
    const ta = taRef.current;
    if (!ta) return;
    e.preventDefault();
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const selected = text.slice(start, end);

    const insertAtCaret = (insert) => {
      setText(text.slice(0, start) + insert + text.slice(end));
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + insert.length;
        ta.setSelectionRange(pos, pos);
      });
    };

    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: <Scissors size={14} />,
          label: 'Cut',
          disabled: !selected,
          onClick: async () => {
            await copyTextToClipboard(selected);
            insertAtCaret('');
          },
        },
        {
          icon: <Copy size={14} />,
          label: 'Copy',
          disabled: !selected,
          onClick: () => copyTextToClipboard(selected),
        },
        {
          icon: <ClipboardPaste size={14} />,
          label: 'Paste',
          onClick: async () => {
            const clip = await readClipboard();
            if (clip.image) {
              const ext = (clip.image.type.split('/')[1] || 'png').split('+')[0];
              stageFile(
                new File([clip.image], `pasted-${Date.now()}.${ext}`, { type: clip.image.type })
              );
            } else if (clip.text) {
              insertAtCaret(clip.text);
            }
          },
        },
        {
          icon: <TextSelect size={14} />,
          label: 'Select all',
          disabled: !text,
          onClick: () => {
            ta.focus();
            ta.select();
          },
        },
      ],
    });
  };

  // Drag & drop a file onto the composer — same staged-pending flow as the
  // paperclip and paste paths.
  const onDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const onDrop = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    setDragOver(false);
    stageFile(e.dataTransfer.files[0]);
  };

  // Insert an emoji at the caret (or at the end, if the textarea never had
  // focus yet) without stomping any existing selection.
  const insertEmoji = (emoji) => {
    const ta = taRef.current;
    const start = ta ? ta.selectionStart ?? text.length : text.length;
    const end = ta ? ta.selectionEnd ?? text.length : text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
    });
  };

  return (
    <div
      className="chat-composer"
      style={{
        position: 'relative',
        ...(dragOver ? { outline: '2px dashed #8869eb', outlineOffset: -4, borderRadius: 8 } : {}),
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {replyTarget && (
        <div className="chat-composer-reply-bar">
          <Reply size={13} className="chat-composer-reply-bar-icon" aria-hidden />
          <span className="chat-composer-reply-bar-text">
            Replying to <strong>{replyTarget.sender_name}</strong> — {replyTarget.body}
          </span>
          <button
            type="button"
            className="chat-composer-reply-bar-close"
            title="Cancel reply"
            onClick={() => useChatStore.getState().clearReplyTarget()}
          >
            <X size={13} />
          </button>
        </div>
      )}
      <div className="chat-composer-tools">
        <Tooltip content="Code block" side="top">
          <button
            className={`chat-tool-btn ${codeMode ? 'chat-tool-btn--on' : ''}`}
            onClick={() => setCodeMode((v) => !v)}
          >
            <Code size={16} />
          </button>
        </Tooltip>
        {codeMode && (
          <CompactSelectPicker
            className="chat-code-lang-picker"
            value={codeLang}
            onChange={setCodeLang}
            options={CODE_LANGUAGE_OPTIONS}
            searchable
            searchPlaceholder="Search languages…"
            menuMinWidth={140}
            aria-label="Code language"
          />
        )}
        {/* Byte budget, shown only once it matters (>75%). The cap is in BYTES,
            so a Cyrillic writer reaches it at ~6 000 characters and a CJK
            writer at ~4 000 — with a character count they would have no warning
            at all. Passing it is not an error (the message becomes a .txt), so
            this is information, not a block. */}
        {showSizeIndicator && bodyBytes > PASTE_TO_FILE_BYTES * 0.75 && (
          <span
            className="chat-byte-budget"
            title={`${bodyBytes.toLocaleString()} of ${PASTE_TO_FILE_BYTES.toLocaleString()} bytes — longer messages are sent as a .txt attachment`}
          >
            {Math.round((bodyBytes / PASTE_TO_FILE_BYTES) * 100)}%
          </span>
        )}
        <Tooltip content={attachTooltip} side="top">
          <button className="chat-tool-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Paperclip size={16} />
          </button>
        </Tooltip>
        <Tooltip content="Share a workspace, collection or request" side="top" disabled={showRefs}>
          <button
            className={`chat-tool-btn ${showRefs ? 'chat-tool-btn--on' : ''}`}
            onClick={() => setShowRefs((v) => !v)}
          >
            <Link2 size={16} />
          </button>
        </Tooltip>
        <Tooltip content="Share an appointment" side="top" disabled={showApptPicker}>
          <button
            className={`chat-tool-btn ${showApptPicker ? 'chat-tool-btn--on' : ''}`}
            onClick={() => setShowApptPicker((v) => !v)}
          >
            <CalendarDays size={16} />
          </button>
        </Tooltip>
        <Tooltip content="Emoji" side="top" disabled={emojiPickerOpen}>
          <button
            ref={emojiTriggerRef}
            className={`chat-tool-btn ${emojiPickerOpen ? 'chat-tool-btn--on' : ''}`}
            onClick={() => setEmojiPickerOpen((v) => !v)}
          >
            <Smile size={16} />
          </button>
        </Tooltip>
        <EmojiReactionPicker
          open={emojiPickerOpen}
          anchorRef={emojiTriggerRef}
          onClose={() => setEmojiPickerOpen(false)}
          onSelect={insertEmoji}
          placement="top"
        />
        {uploading && compressPct == null && (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Uploading…</span>
        )}
        {compressPct != null && (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Compressing video… {compressPct}%</span>
        )}
        {uploadError && !uploading && (
          <span style={{ fontSize: 12, color: '#ef4444' }}>Upload failed</span>
        )}
        <input ref={fileRef} type="file" hidden accept={ATTACH_EXTS.join(',')} onChange={onFile} />
      </div>

      {showRefs && (
        <ShareDrilldownPicker
          channelId={channelId}
          onShare={sendShare}
          onClose={() => setShowRefs(false)}
        />
      )}

      {showApptPicker && (
        <AppointmentSharePicker
          channelId={channelId}
          onClose={() => setShowApptPicker(false)}
        />
      )}

      {pending && (
        <div className="chat-attach-pending">
          <div className="chat-attach-pending-item">
            <button
              type="button"
              className="chat-attach-pending-remove"
              title="Remove attachment"
              onClick={clearPending}
            >
              <X size={13} />
            </button>
            {pending.isImage ? (
              <img className="chat-attach-pending-img" src={pending.previewUrl} alt={pending.name} />
            ) : (
              <div className="chat-attach-pending-file">
                <FileText size={16} />
                <span className="chat-attach-pending-name">{pending.name}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="chat-composer-row">
        <textarea
          ref={taRef}
          className={`chat-composer-input ${codeMode ? 'chat-composer-input--code' : ''}`}
          placeholder={
            pending
              ? 'Add a caption… (Enter to send)'
              : codeMode
              ? 'Code snippet… (Cmd/Ctrl+Enter to send)'
              : 'Message… (Enter to send)'
          }
          value={text}
          onChange={onType}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onContextMenu={onComposerContextMenu}
          rows={1}
        />
      </div>

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={ctxMenu.items}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}
